import { PayloadAction } from '@reduxjs/toolkit';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, Observer } from 'rxjs';
import { filter, ignoreElements, mergeMap, tap } from 'rxjs/operators';

import configLib from '../../../configLib';
import { SymbolAndOptionalExchangeCode } from '../../models/fundamental/types';
import {
  GWMarketDataSummaryBucket,
} from '../../models/gateway/types';
import GlobalService from '../../services/Global.service';
import { isEmpty } from '../../util/typeGuards';
import { uploadUserPreferencesData } from '../app';
import { UserPreferencesData } from '../app/types';
import {
  addOrRemoveSymbolToFavoriteList,
  addSymbolToRecentlyViewed,
  gatewayConnected,
  individualExtendedInfoSuccess,
  reconnect,
  reconnectEventAction,
  ReconnectPayload,
  subscribeSymbolPriceAndChartData,
} from '../common-actions';
import { accountsCompleted } from '../crm';
import {
  getNewStocks,
  getPopularStocks,
} from '../fundamental';
import {
  getAccountPendingOrdersSuccess,
  getAccountTradeHistory,
  getAccountTradeHistorySuccess,
  getOpenPositions,
  getOpenPositionsSuccess,
} from '../reporting';
import { PENDING_ORDERS_REQUEST_TENNSTATUS } from '../reporting/constants';
import { getFavoriteStocks, getRecentlyViewedStocks, hasValidCookie, isGatewayMDFullyEnabled } from '../selectors';

import {
  chartSubscription,
  instrumentSnapshotSubscription,
  marketDataSubscribeTradeSubscription,
  symbolStatusSubscription,
} from './subscriptionHelpers';
import { AddSymbolToRecentlyViewedPayload, SymbolFavoriteListActionType, SymbolFavoriteListPayload } from './types';
import { retrieveFavoriteStocksFromServerCompleted } from '.';

type MarketDataEpicState = Parameters<typeof hasValidCookie>[0];

const connectEpic = (action$: Observable<any>, state$: StateObservable<MarketDataEpicState>) => action$.pipe(
  ofType(individualExtendedInfoSuccess.type, reconnect.type),
  filter(() => !(isGatewayMDFullyEnabled())), // disable market data if gw is enabled for MD feeds
  filter(action => {
    if (state$.value.marketData.connected) {
      console.warn('[market-data/connectEpic] Already connected!');
      return false;
    }
    return true;
  }),
  filter(action => (action.type !== reconnect.type || ((action.payload as ReconnectPayload).marketData ?? false))),
  filter(action => {
    // ART-443 Temporary fix for web deployment
    if (configLib.isWeb && !configLib.__DEV__) {
      console.warn('[market-data/connectEpic] Skip connecting MD for Web');
      return false;
    }
    return true;
  }),
  tap(action => GlobalService.marketDataService.connect()),
  ignoreElements(),
);

/**
 * Loads all initial lists of symbols. Later on used by `loadSymbolDataForInitialListsEpic`
 */
const loadInitialSymbolListsEpic = (
  action$: Observable<any>,
  state$: StateObservable<MarketDataEpicState>,
) => action$.pipe(
  ofType(gatewayConnected.type, accountsCompleted.type), // TODO: Rename `accountsCompleted` to `getAccountsCompleted`
  filter(() => !state$.value.marketData.isInitialPriceDataLoaded),
  filter(() => hasValidCookie(state$.value)),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    switch (action.type) {
      case gatewayConnected.type: {
        // watchlist (popular)
        observer.next(getPopularStocks());

        // discover (new on the market, popular groups (sectors), divident payers)
        observer.next(getNewStocks());
        break;
      }

      case accountsCompleted.type: {
        // my account (my stocks, pending orders)
        observer.next(getOpenPositions());
        observer.next(getAccountTradeHistory({
          tenNStatuses_StringList: PENDING_ORDERS_REQUEST_TENNSTATUS,
          isPendingOrdersCall: true,
          caller: 'loadInitialSymbolListsEpic',
        }));
        break;
      }

      default:
        break;
    }
  })),
);

const loadOnlyInitialSymbolListsEpic = (
  action$: Observable<any>,
  state$: StateObservable<MarketDataEpicState>,
) => action$.pipe(
  ofType(reconnectEventAction.type),
  filter(() => hasValidCookie(state$.value)),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    // watchlist (popular)
    observer.next(getPopularStocks());

    // my stocks
    observer.next(getOpenPositions());

    // discover (new on the market)
    observer.next(getNewStocks());
  })),
);

const addOrRemoveSymbolToFavoriteListEpic = (
  action$: Observable<PayloadAction<SymbolFavoriteListPayload>>,
  state$: StateObservable<MarketDataEpicState>,
) => action$.pipe(
  ofType(addOrRemoveSymbolToFavoriteList.type),
  tap(action => {
    const actionPayload: SymbolFavoriteListPayload = action.payload;
    const { symbolAndExchange: rawSymbolAndExchange, actionType } = actionPayload;
    const isRemoving = actionType === SymbolFavoriteListActionType.Remove;

    if (isEmpty(rawSymbolAndExchange)) {
      return;
    }

    const symbolAndExchange: SymbolAndOptionalExchangeCode = { ...rawSymbolAndExchange };
    const mappedSymbols: SymbolAndOptionalExchangeCode[] = [ symbolAndExchange ];
    // InstrumentSnapshot only. Trade and chart for the favourites list are owned by
    // FavouritesWatchList via useSubscriptionManager - sending them here too double-subscribes.
    instrumentSnapshotSubscription(mappedSymbols, isRemoving);
  }),
  ignoreElements(),
);

const subscribeSymbolPriceAndChartDataEpic = (
  action$: Observable<any>,
  state$: StateObservable<MarketDataEpicState>,
) => action$.pipe(
  ofType(
    subscribeSymbolPriceAndChartData.type,

    // reporting
    getOpenPositionsSuccess.type,
    getAccountTradeHistorySuccess.type,
    getAccountPendingOrdersSuccess.type,
  ),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const { payload } = action;
    let mappedSymbols: SymbolAndOptionalExchangeCode[] = payload as SymbolAndOptionalExchangeCode[];

    switch (action.type) {
      case getAccountTradeHistorySuccess.type: {
        const { tradeHistoryOrders } = state$.value.reporting ?? {};
        const companyLogoAndName = state$.value.fundamental.companyLogoAndName ?? {};

        mappedSymbols = tradeHistoryOrders?.map(
          item => {
            const companyLogoAndNameElement = companyLogoAndName[item.symbol] ?? {};
            return ({ symbol: item.symbol, stockExchangeCode: companyLogoAndNameElement.stockExchangeCode });
          },
        );
        break;
      }

      case getOpenPositionsSuccess.type: {
        const { openPositions } = state$.value.reporting ?? {};
        const companyLogoAndName = state$.value.fundamental.companyLogoAndName ?? {};

        mappedSymbols = openPositions?.map(
          item => {
            const companyLogoAndNameElement = companyLogoAndName[item.symbol] ?? {};
            return ({ symbol: item.symbol, stockExchangeCode: companyLogoAndNameElement.stockExchangeCode });
          },
        );
        break;
      }

      case getAccountPendingOrdersSuccess.type: {
        const { pendingOrders } = state$.value.reporting ?? {};
        const companyLogoAndName = state$.value.fundamental.companyLogoAndName ?? {};

        mappedSymbols = pendingOrders?.map(
          item => {
            const companyLogoAndNameElement = companyLogoAndName[item.symbol] ?? {};
            return ({ symbol: item.symbol, stockExchangeCode: companyLogoAndNameElement.stockExchangeCode });
          },
        );
        break;
      }

      default:
        // Use the default mappedSymbols (payload as SymbolAndOptionalExchangeCode[])
        break;
    }

    if (isEmpty(mappedSymbols)) {
      return;
    }

    instrumentSnapshotSubscription(mappedSymbols);
    marketDataSubscribeTradeSubscription(mappedSymbols);
    symbolStatusSubscription(mappedSymbols);
    chartSubscription(
      mappedSymbols,
      [ GWMarketDataSummaryBucket.OneDay, GWMarketDataSummaryBucket.ThreeMonths ],
    );
  })),
);

const sendFavoriteStocksToServerEpic = (
  action$: Observable<any>,
  state$: StateObservable<MarketDataEpicState>,
) => action$.pipe(
  ofType(addOrRemoveSymbolToFavoriteList.type),
  mergeMap(
    action => new Observable((observer: Observer<any>) => {
      const fileDataJson: UserPreferencesData = {
        ...state$.value.app.userPreferencesData,
        favouriteStocks: getFavoriteStocks(state$.value),
      };
      observer.next(uploadUserPreferencesData(fileDataJson));
    }),
  ),
);

const sendRecentlyViewedStocksToServerEpic = (
  action$: Observable<PayloadAction<AddSymbolToRecentlyViewedPayload>>,
  state$: StateObservable<MarketDataEpicState>,
) => action$.pipe(
  ofType(addSymbolToRecentlyViewed.type),
  mergeMap(
    action => new Observable((observer: Observer<ReturnType<typeof uploadUserPreferencesData>>) => {
      const fileDataJson: UserPreferencesData = {
        ...state$.value.app.userPreferencesData,
        recentlyViewedStocks: getRecentlyViewedStocks(state$.value),
      };
      observer.next(uploadUserPreferencesData(fileDataJson));
    }),
  ),
);

const subscribeRecentlyViewedSymbolEpic = (
  action$: Observable<PayloadAction<AddSymbolToRecentlyViewedPayload>>,
  state$: StateObservable<MarketDataEpicState>,
) => action$.pipe(
  ofType(addSymbolToRecentlyViewed.type),
  tap(action => {
    const { symbolAndExchange: rawSymbolAndExchange } = action.payload;
    if (isEmpty(rawSymbolAndExchange)) {
      return;
    }

    instrumentSnapshotSubscription([ { ...rawSymbolAndExchange } ]);
  }),
  ignoreElements(),
);

const subscribeRecentlyViewedFromServerEpic = (
  action$: Observable<ReturnType<typeof retrieveFavoriteStocksFromServerCompleted>>,
  state$: StateObservable<MarketDataEpicState>,
) => action$.pipe(
  ofType(retrieveFavoriteStocksFromServerCompleted.type),
  tap(() => {
    const recentlyViewedStocks = getRecentlyViewedStocks(state$.value);

    if (isEmpty(recentlyViewedStocks)) {
      return;
    }

    instrumentSnapshotSubscription(recentlyViewedStocks);
  }),
  ignoreElements(),
);

export default combineEpics(
  connectEpic,
  subscribeSymbolPriceAndChartDataEpic,
  loadInitialSymbolListsEpic,
  loadOnlyInitialSymbolListsEpic,
  addOrRemoveSymbolToFavoriteListEpic,
  sendFavoriteStocksToServerEpic,
  sendRecentlyViewedStocksToServerEpic,
  subscribeRecentlyViewedSymbolEpic,
  subscribeRecentlyViewedFromServerEpic,
);
